import React from 'react';
import { Link } from 'react-router-dom';
import { FileText, AlertTriangle, Clock, DollarSign, ArrowLeft } from 'lucide-react';
import { Button } from '../components/ui/button';

const TermsPage = () => {
  return (
    <div className="min-h-screen bg-white pt-24" data-testid="terms-page">
      {/* Hero Section */}
      <section className="px-6 md:px-12 py-16 border-b border-gray-200">
        <div className="max-w-[1400px] mx-auto">
          <Link 
            to="/" 
            className="inline-flex items-center gap-2 text-gray-500 hover:text-[#C6A87C] transition-colors mb-8"
          >
            <ArrowLeft className="w-4 h-4" />
            Back to Home
          </Link>
          <div className="flex items-center gap-4 mb-6">
            <div className="w-16 h-16 bg-[#C6A87C]/10 flex items-center justify-center">
              <FileText className="w-8 h-8 text-[#C6A87C]" strokeWidth={1.5} />
            </div>
            <div>
              <p className="text-sm text-[#C6A87C] uppercase tracking-widest mb-1">Legal</p>
              <h1 className="font-['Barlow_Condensed'] text-4xl md:text-5xl font-bold uppercase tracking-tight text-gray-900">
                Terms of Service
              </h1>
            </div>
          </div>
          <p className="text-gray-500">
            Last updated: December 2024 | Effective Date: December 1, 2024
          </p>
        </div>
      </section>

      {/* Terms Content */}
      <section className="px-6 md:px-12 py-16">
        <div className="max-w-4xl mx-auto">
          {/* Introduction */}
          <div className="prose max-w-none mb-12">
            <h2 className="font-['Barlow_Condensed'] text-2xl font-bold uppercase tracking-tight mb-4 text-gray-900">
              1. Agreement to Terms
            </h2>
            <p className="text-gray-600 mb-6">
              By accessing or using RideGear Rentals ("Service"), you agree to be bound by these Terms of Service ("Terms"). 
              If you do not agree to these Terms, you may not use the Service. These Terms apply to all users, including 
              renters, visitors, and others who access the Service.
            </p>
            <p className="text-gray-600 mb-6">
              RideGear Rentals provides motorcycle protective gear rentals including but not limited to helmets, jackets, 
              gloves, and related accessories. By renting equipment through our Service, you acknowledge that you have read, 
              understood, and agree to be bound by these Terms.
            </p>
          </div>

          {/* Liability Waiver - Highlighted Section */}
          <div className="bg-destructive/10 border border-destructive/30 p-8 mb-12" data-testid="liability-waiver">
            <div className="flex items-start gap-4 mb-4">
              <AlertTriangle className="w-8 h-8 text-destructive flex-shrink-0 mt-1" />
              <h2 className="font-['Barlow_Condensed'] text-2xl font-bold uppercase tracking-tight text-gray-900">
                2. Liability Waiver & Assumption of Risk
              </h2>
            </div>
            <div className="space-y-4 text-gray-600">
              <p>
                <strong className="text-gray-900">IMPORTANT: READ CAREFULLY BEFORE RENTING.</strong> Motorcycle riding is an inherently 
                dangerous activity that involves significant risk of personal injury, property damage, and death. By renting 
                protective gear from RideGear Rentals, you acknowledge and accept these risks.
              </p>
              <p>
                <strong className="text-gray-900">Assumption of Risk:</strong> You voluntarily assume all risks associated with 
                motorcycle riding and the use of rented protective gear, including but not limited to: accidents, collisions, 
                equipment malfunction, weather conditions, road hazards, and the actions of other motorists.
              </p>
              <p>
                <strong className="text-gray-900">Limitation of Liability:</strong> To the fullest extent permitted by law, RideGear 
                Rentals, its owners, officers, employees, agents, and affiliates shall not be liable for any direct, indirect, 
                incidental, special, consequential, or punitive damages arising from:
              </p>
              <ul className="list-disc list-inside space-y-2 ml-4">
                <li>Your use of rented protective gear</li>
                <li>Any injury, death, or property damage occurring while using or wearing the gear</li>
                <li>Equipment failure or malfunction, regardless of cause</li>
                <li>Improper fit or use of the gear</li>
                <li>Any motorcycle-related activity</li>
              </ul>
              <p>
                <strong className="text-gray-900">Indemnification:</strong> You agree to indemnify, defend, and hold harmless 
                RideGear Rentals and its affiliates from any claims, damages, losses, or expenses (including attorney fees) 
                arising from your use of the Service, violation of these Terms, or violation of any third-party rights.
              </p>
              <p>
                <strong className="text-gray-900">No Guarantee of Protection:</strong> While our gear meets or exceeds CE safety 
                certifications, no protective equipment can guarantee prevention of injury in all circumstances. Gear provides 
                protection under specific conditions and may not prevent all injuries.
              </p>
            </div>
          </div>

          {/* Late Fee Policy - Highlighted Section */}
          <div className="bg-[#C6A87C]/10 border border-[#C6A87C]/30 p-8 mb-12" data-testid="late-fee-policy">
            <div className="flex items-start gap-4 mb-4">
              <Clock className="w-8 h-8 text-[#C6A87C] flex-shrink-0 mt-1" />
              <h2 className="font-['Barlow_Condensed'] text-2xl font-bold uppercase tracking-tight text-gray-900">
                3. Late Fee Policy
              </h2>
            </div>
            <div className="space-y-4 text-gray-600">
              <p>
                All rentals include a <strong className="text-gray-900">2-day buffer period</strong> after your rental end date for 
                shipping and processing. Gear must be dropped off at a UPS or FedEx location by the return date shown in your 
                rental confirmation.
              </p>
              <div className="bg-gray-50 p-4 border border-gray-200">
                <p className="font-semibold text-gray-900 mb-2">Late Return Charges:</p>
                <ul className="space-y-2">
                  <li>
                    <span className="text-[#C6A87C] font-bold">1.5x Daily Rate</span> per day for each day the gear is returned 
                    late (after the buffer period)
                  </li>
                  <li>
                    Late fees are charged to the card on file automatically
                  </li>
                  <li>
                    After 7 days late, the gear will be considered lost and replacement costs will apply
                  </li>
                </ul>
              </div>
              <p>
                <strong className="text-gray-900">Extensions:</strong> If you need to extend your rental, contact us before your 
                return date. Extensions are subject to availability and charged at the standard daily rate (not the late fee rate).
              </p>
              <p>
                <strong className="text-gray-900">Grace Period:</strong> We understand shipping delays happen. If your return is 
                delayed due to carrier issues (with tracking proof), contact support within 24 hours for fee waiver consideration.
              </p>
            </div>
          </div>

          {/* Replacement Cost Policy - Highlighted Section */}
          <div className="bg-gray-50 border border-gray-200 p-8 mb-12" data-testid="replacement-cost">
            <div className="flex items-start gap-4 mb-4">
              <DollarSign className="w-8 h-8 text-[#C6A87C] flex-shrink-0 mt-1" />
              <h2 className="font-['Barlow_Condensed'] text-2xl font-bold uppercase tracking-tight text-gray-900">
                4. Replacement Cost Clause
              </h2>
            </div>
            <div className="space-y-4 text-gray-600">
              <p>
                You are responsible for the gear from the moment of delivery until it is received back at our facility. 
                By renting from RideGear, you agree to pay the replacement cost for any gear that is:
              </p>
              <ul className="list-disc list-inside space-y-2 ml-4">
                <li><strong className="text-gray-900">Lost:</strong> Not returned within 7 days of the return date</li>
                <li><strong className="text-gray-900">Stolen:</strong> Reported stolen and not recovered</li>
                <li><strong className="text-gray-900">Damaged Beyond Repair:</strong> Damaged to the extent that repair is not economically feasible</li>
              </ul>
              
              <div className="bg-gray-100/50 p-4 mt-4">
                <p className="font-semibold text-gray-900 mb-3">Typical Replacement Costs:</p>
                <div className="grid grid-cols-2 gap-4 text-sm">
                  <div>
                    <p className="text-gray-600">Premium Helmets</p>
                    <p className="text-gray-900 font-semibold">$800 - $1,800</p>
                  </div>
                  <div>
                    <p className="text-gray-600">Racing Jackets</p>
                    <p className="text-gray-900 font-semibold">$500 - $900</p>
                  </div>
                  <div>
                    <p className="text-gray-600">Racing Gloves</p>
                    <p className="text-gray-900 font-semibold">$200 - $500</p>
                  </div>
                  <div>
                    <p className="text-gray-600">Complete Kits</p>
                    <p className="text-gray-900 font-semibold">$1,500 - $3,200</p>
                  </div>
                </div>
              </div>

              <p>
                <strong className="text-gray-900">Damage Assessment:</strong> Normal wear is expected and covered. Damage includes: 
                cracks, deep scratches, torn materials, broken components, water damage, and excessive dirt/stains beyond normal cleaning.
              </p>
              <p>
                <strong className="text-gray-900">Insurance:</strong> We recommend checking if your homeowner's or renter's insurance 
                covers rented equipment. Travel insurance may also provide coverage for lost or stolen items.
              </p>
            </div>
          </div>

          {/* Additional Sections */}
          <div className="space-y-8">
            <div>
              <h2 className="font-['Barlow_Condensed'] text-2xl font-bold uppercase tracking-tight mb-4 text-gray-900">
                5. Rental Agreement
              </h2>
              <div className="text-gray-600 space-y-4">
                <p>By placing a rental order, you agree to:</p>
                <ul className="list-disc list-inside space-y-2 ml-4">
                  <li>Provide accurate personal and payment information</li>
                  <li>Use the gear only for its intended purpose (motorcycle riding protection)</li>
                  <li>Not modify, alter, or tamper with the gear in any way</li>
                  <li>Not sublet, lend, or transfer the gear to any third party</li>
                  <li>Return all gear including bags, labels, and accessories provided</li>
                  <li>Report any damage or issues within 24 hours of discovery</li>
                </ul>
              </div>
            </div>

            <div>
              <h2 className="font-['Barlow_Condensed'] text-2xl font-bold uppercase tracking-tight mb-4 text-gray-900">
                6. Security Deposit
              </h2>
              <div className="text-gray-600 space-y-4">
                <p>
                  A security deposit hold (typically 20-30% of gear retail value) will be placed on your payment method at checkout. 
                  This hold:
                </p>
                <ul className="list-disc list-inside space-y-2 ml-4">
                  <li>Is not charged unless damage, loss, or late fees apply</li>
                  <li>Is released within 3-5 business days after gear inspection</li>
                  <li>May temporarily reduce your available credit limit</li>
                  <li>Authorizes charges for any applicable fees</li>
                </ul>
              </div>
            </div>

            <div>
              <h2 className="font-['Barlow_Condensed'] text-2xl font-bold uppercase tracking-tight mb-4 text-gray-900">
                7. Cancellation & Refunds
              </h2>
              <div className="text-gray-600 space-y-4">
                <p>
                  <strong className="text-gray-900">Before Shipment:</strong> Full refund if cancelled before gear ships.
                </p>
                <p>
                  <strong className="text-gray-900">After Shipment:</strong> No refund for the rental period. You may return gear 
                  early but rental fees are not prorated.
                </p>
                <p>
                  <strong className="text-gray-900">Size Exchanges:</strong> One free size exchange within 24 hours of delivery. 
                  Additional exchanges may incur shipping charges.
                </p>
              </div>
            </div>

            <div>
              <h2 className="font-['Barlow_Condensed'] text-2xl font-bold uppercase tracking-tight mb-4 text-gray-900">
                8. Privacy & Data
              </h2>
              <div className="text-gray-600 space-y-4">
                <p>
                  Your personal information is collected and used in accordance with our Privacy Policy. We collect only 
                  information necessary to process rentals and improve our Service. We do not sell your personal data to third parties.
                </p>
              </div>
            </div>

            <div>
              <h2 className="font-['Barlow_Condensed'] text-2xl font-bold uppercase tracking-tight mb-4 text-gray-900">
                9. Governing Law & Disputes
              </h2>
              <div className="text-gray-600 space-y-4">
                <p>
                  These Terms are governed by the laws of the State of California, without regard to conflict of law principles. 
                  Any disputes shall be resolved through binding arbitration in San Francisco, California, unless you opt out 
                  within 30 days of first using the Service.
                </p>
              </div>
            </div>

            <div>
              <h2 className="font-['Barlow_Condensed'] text-2xl font-bold uppercase tracking-tight mb-4 text-gray-900">
                10. Contact Information
              </h2>
              <div className="text-gray-600 space-y-4">
                <p>For questions about these Terms, contact us at:</p>
                <p>
                  <strong className="text-gray-900">RideGear Rentals</strong><br />
                  Email: legal@ridegear.com<br />
                  Support: support@ridegear.com
                </p>
              </div>
            </div>
          </div>

          {/* Acceptance Notice */}
          <div className="mt-12 p-6 bg-[#C6A87C]/10 border border-[#C6A87C]/30">
            <p className="text-gray-900 font-semibold">
              By checking "I agree to the Terms of Service" during checkout, you acknowledge that you have read, understood, 
              and agree to be bound by all terms and conditions stated herein, including the Liability Waiver, Late Fee Policy, 
              and Replacement Cost Clause.
            </p>
          </div>

          {/* Back to Top */}
          <div className="mt-12 text-center">
            <Link to="/faq">
              <Button 
                variant="outline"
                className="border-gray-200 hover:border-[#C6A87C] hover:text-[#C6A87C] uppercase tracking-wider"
              >
                View FAQ
              </Button>
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default TermsPage;
